import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { ChatAnswer, ChatMessage } from '../model/chat';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ]
})
export class ChatWidgetComponent {
  public readonly Constant = Constant;
  public isOpen = false;
  // 画面の入力欄（チャット以外）にカーソルがあるか。iPhone ではキーボードを出すとボタンが上がってきて入力の邪魔になるため、SP では隠す
  public isTypingInPage = false;
  public isSending = false;
  public inputText = '';
  public messages: ChatMessage[] = [];

  // 会話は画面を開いている間だけ保持する（履歴の復元は行わない）
  private conversationId = '';

  @ViewChild('messageArea') private messageArea?: ElementRef<HTMLDivElement>;

  constructor(
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
  ) { }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    this.isTypingInPage = !!target
      && !target.closest('app-chat-widget')
      && (target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT'
        || (target.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'file'].includes((target as HTMLInputElement).type)));
  }

  @HostListener('document:focusout')
  onDocumentFocusOut(): void {
    this.isTypingInPage = false;
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  onToggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.scrollToBottom();
    }
  }

  onSend(): void {
    const message = this.inputText.trim();
    if (!message || this.isSending) return;
    if (message.length > Constant.fvMaxChatInput) return;
    if (!this.commonUtil.checkOnline()) return;

    this.messages.push({ role: 'user', content: message });
    this.inputText = '';
    this.isSending = true;
    this.scrollToBottom();

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      options['body'] = {
        message,
        conversationId: this.conversationId || null,
      };
      this.commonUtil.apiPost(Constant.apiPathChat, options)
        .then(res => {
          const data: ChatAnswer = res.data;
          this.conversationId = data.conversationId ?? '';
          // 根拠にしたFAQは画面には出さない（サーバ側でログには記録している）
          this.messages.push({
            role: 'assistant',
            content: data.answer,
            isAnswered: data.isAnswered,
          });
          this.scrollToBottom();
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            // 送信した質問を戻して、そのまま送り直せるようにする
            this.messages.pop();
            this.inputText = message;
            this.toastUtil.showErrorToast('', this.getErrorMessage(err), Constant.toastShowMiliSecErr);
          }
        })
        .finally(() => {
          this.isSending = false;
        });
    });
  }

  // Enter で送信、Shift+Enter は改行
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    this.onSend();
  }

  // サーバからの業務エラーはその文言を、通信エラーは共通文言を表示する
  private getErrorMessage(err: any): string {
    if (err?.status === Constant.NG && err.message) {
      return err.message;
    }
    return Constant.msgNetworkError;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.messageArea?.nativeElement;
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    });
  }
}
